import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiPlus, FiCalendar, FiDollarSign, FiPaperclip, FiTrash2, FiCreditCard, FiCopy, FiCheckCircle } from 'react-icons/fi';
const DespesaModal = ({ isOpen, onClose, onSave, despesaToEdit, initialCategoria = 'DESPESA_FIXA' }) => {
  const isEditMode = !!(despesaToEdit && despesaToEdit.id);
  const isCloneMode = !!(despesaToEdit && !despesaToEdit.id);
  const getInitialState = () => ({
    descricao: '',
    valor: '',
    dataVencimento: new Date().toISOString().split('T')[0],
    dataPagamento: '',
    categoria: initialCategoria,
    subcategoria: '',
    status: 'PENDENTE',
    anexoUrl: '',
    anexoArquivo: null,
    usarFormaPagamento: false,
    formaPagamento: 'A_VISTA',
    totalParcelas: 2
  });

  const [formData, setFormData] = useState(getInitialState());
  const [loading, setLoading] = useState(false);
  const [jaPago, setJaPago] = useState(false);

  useEffect(() => {
    if (despesaToEdit) {
      setFormData({
        ...despesaToEdit,
        dataVencimento: despesaToEdit.dataVencimento ? despesaToEdit.dataVencimento.split('T')[0] : '',
        dataPagamento: despesaToEdit.dataPagamento ? despesaToEdit.dataPagamento.split('T')[0] : '',
        anexoUrl: despesaToEdit.anexoUrl || '',
        anexoArquivo: null,
        usarFormaPagamento: !!despesaToEdit.formaPagamento,
        formaPagamento: despesaToEdit.formaPagamento || 'A_VISTA',
        totalParcelas: despesaToEdit.totalParcelas || 2
      });
    } else {
      setFormData(getInitialState());
    }
    setJaPago(false); // Reset ao abrir/reabrir o modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [despesaToEdit, isOpen, initialCategoria]);

  // Auto-toggle "já pago" quando forma de pagamento muda
  useEffect(() => {
    if (!isEditMode && formData.usarFormaPagamento) {
      setJaPago(formData.formaPagamento === 'A_VISTA');
    }
    if (!formData.usarFormaPagamento) {
      setJaPago(false);
    }
  }, [formData.formaPagamento, formData.usarFormaPagamento, isEditMode]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e, addAnother = false) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      // 1. Cria a cópia do formulário
      const payload = { ...formData };

      // 2. Trata a vírgula para ponto no valor monetário, se necessário
      if (typeof payload.valor === 'string') {
        payload.valor = parseFloat(payload.valor.replace(',', '.')) || 0;
      } else {
        payload.valor = parseFloat(payload.valor) || 0;
      }

      // === 3. A MÁGICA DA CORREÇÃO AQUI ===
      // Transforma string vazia em nulo para o Spring Boot não surtar com os Enums/Datas
      if (payload.subcategoria === "") {
        payload.subcategoria = null;
      }
      if (payload.dataPagamento === "") {
        payload.dataPagamento = null;
      }

      // 4. Forma de pagamento / parcelamento
      if (!payload.usarFormaPagamento) {
        payload.formaPagamento = null;
        payload.totalParcelas = null;
      } else if (payload.formaPagamento === 'PARCELADO') {
        const n = parseInt(payload.totalParcelas, 10);
        payload.totalParcelas = (Number.isFinite(n) && n > 1) ? n : 2;
      } else {
        payload.totalParcelas = null;
      }
      delete payload.usarFormaPagamento;

      // 4b. Auto-baixa: se marcou "já pago", injeta status PAGO + data de pagamento
      if (!isEditMode && jaPago) {
        payload.status = 'PAGO';
        payload.dataPagamento = payload.dataVencimento || new Date().toISOString().split('T')[0];
      }

      // 5. Salva os dados limpos
      await onSave(payload, isEditMode ? despesaToEdit.id : null);

      // 5. Continua ou fecha
      if (addAnother && !isEditMode && !isCloneMode) {
        // Mantém a data e a categoria para agilizar a próxima digitação
        setFormData({
          ...getInitialState(),
          dataVencimento: formData.dataVencimento,
          categoria: formData.categoria
        });
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar despesa', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-xl font-black text-gray-800 flex items-center gap-2">
            {isCloneMode ? <FiCopy className="text-slate-500" /> : <FiDollarSign className="text-blue-600" />}
            {isEditMode ? 'Editar Despesa' : isCloneMode ? 'Duplicar Despesa' : 'Nova Despesa'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors">
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto hidden-scrollbar">
          <form id="despesa-form" className="space-y-4" onSubmit={(e) => handleSubmit(e, false)}>

            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Descrição</label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                required
                placeholder="Ex: Aluguel do Escritório"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Valor (R$)</label>
                <input
                  type="number"
                  name="valor"
                  step="0.01"
                  value={formData.valor}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Categoria</label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700 bg-white"
                >
                  <option value="DESPESA_FIXA">Despesa Fixa</option>
                  <option value="DESPESA_VARIAVEL">Despesa Variável</option>
                  <option value="IMPOSTO">Imposto</option>
                  <option value="PESSOAL">Pessoal / RH</option>
                  <option value="COMPRA_NACIONAL">Compra Nacional</option>
                  <option value="INVESTIMENTO">Investimento</option>
                  <option value="OUTROS">Outros</option>
                </select>
              </div>

              {formData.categoria === 'DESPESA_FIXA' && (
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Subcategoria</label>
                  <select
                    name="subcategoria"
                    value={formData.subcategoria || ''}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700 bg-white"
                  >
                    <option value="" disabled>Selecione...</option>
                    <option value="OPERACIONAL">Operacional</option>
                    <option value="SISTEMAS">Sistemas</option>
                    <option value="ASSESSORIAS">Assessorias</option>
                    <option value="ALIMENTACAO">Alimentação</option>
                    <option value="LIMPEZA">Limpeza</option>
                    <option value="SEGURANCA">Segurança</option>
                    <option value="EXTRAS">Extras</option>
                    <option value="PLATAFORMAS_ADS_FULL">Plataformas / Ads (Full)</option>
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1"><FiCalendar /> Vencimento</label>
                <input
                  type="date"
                  name="dataVencimento"
                  value={formData.dataVencimento}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700"
                />
              </div>

              {isEditMode && (
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1"><FiCalendar /> Pagamento</label>
                  <input
                    type="date"
                    name="dataPagamento"
                    value={formData.dataPagamento || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700"
                  />
                </div>
              )}
            </div>

            {!isEditMode && (
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData.usarFormaPagamento}
                    onChange={(e) => setFormData(prev => ({ ...prev, usarFormaPagamento: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-slate-900 focus:ring-slate-400 cursor-pointer"
                  />
                  <span className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <FiCreditCard /> Adicionar forma de pagamento?
                  </span>
                </label>

                {formData.usarFormaPagamento && (
                  <div className="grid grid-cols-2 gap-4 pt-1">
                    <div>
                      <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Forma</label>
                      <select
                        name="formaPagamento"
                        value={formData.formaPagamento}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700 bg-white"
                      >
                        <option value="A_VISTA">À Vista</option>
                        <option value="PARCELADO">Parcelado</option>
                      </select>
                    </div>

                    {/* Toggle "Já pago?" — aparece apenas para À Vista */}
                    {formData.formaPagamento !== 'PARCELADO' && (
                      <label className="col-span-2 flex items-center gap-3 cursor-pointer select-none pt-1">
                        <input
                          type="checkbox"
                          checked={jaPago}
                          onChange={(e) => setJaPago(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-400 cursor-pointer"
                        />
                        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
                          <FiCheckCircle size={14} className={jaPago ? 'text-emerald-500' : 'text-gray-400'} />
                          Marcar como pago agora?
                        </span>
                        {jaPago && (
                          <span className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md uppercase">
                            Baixa automática
                          </span>
                        )}
                      </label>
                    )}

                    {formData.formaPagamento === 'PARCELADO' && (
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Qtd. de Parcelas</label>
                        <input
                          type="number"
                          name="totalParcelas"
                          min="2"
                          max="60"
                          step="1"
                          value={formData.totalParcelas}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700"
                        />
                      </div>
                    )}

                    {formData.formaPagamento === 'PARCELADO' && Number(formData.valor) > 0 && Number(formData.totalParcelas) > 1 && (
                      <div className="col-span-2 text-xs text-gray-500 font-medium">
                        Serão geradas <span className="font-bold text-gray-700">{Number(formData.totalParcelas)}</span> despesas
                        de aproximadamente{' '}
                        <span className="font-bold text-gray-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                            (Number(formData.valor) / Number(formData.totalParcelas)) || 0
                          )}
                        </span>{' '}
                        cada, com vencimentos mensais consecutivos a partir de {formData.dataVencimento || '...'}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {isEditMode && (
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-medium text-gray-700 bg-white"
                >
                  <option value="PENDENTE">Pendente</option>
                  <option value="PAGO">Pago</option>
                  <option value="CANCELADO">Cancelado</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                <FiPaperclip /> Anexo / Comprovante
              </label>
              {(formData.anexoUrl || formData.anexoArquivo) ? (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg shrink-0">
                      <FiPaperclip size={18} />
                    </div>
                    <a
                      href={formData.anexoArquivo ? URL.createObjectURL(formData.anexoArquivo) : formData.anexoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold text-blue-600 hover:underline truncate"
                    >
                      {formData.anexoArquivo ? formData.anexoArquivo.name : 'Visualizar Anexo Atual'}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, anexoUrl: '', anexoArquivo: null }))}
                    className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors shrink-0"
                  >
                    <FiTrash2 size={14} /> Remover
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    name="anexoArquivo"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) setFormData(prev => ({ ...prev, anexoArquivo: file }));
                    }}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2.5 file:px-4
                      file:rounded-xl file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100 file:transition-colors
                      border border-gray-200 rounded-xl p-1 bg-white cursor-pointer"
                  />
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 justify-end items-center">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancelar
          </button>

          {!isEditMode && !isCloneMode && (
            <button
              type="button"
              disabled={loading}
              onClick={(e) => handleSubmit(e, true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-xl font-bold transition-all disabled:opacity-50"
            >
              <FiPlus size={16} /> Salvar & Adicionar Outro
            </button>
          )}

          <button
            type="submit"
            form="despesa-form"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-sm transition-all disabled:opacity-50"
          >
            <FiSave size={16} /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DespesaModal;